import React, { useEffect, useRef, useState } from 'react';

import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  TextSize,
  ValidationState,
  getIconPixels,
  textSizes,
} from '@/theme/size-tokens';

export type EditableTextProps = {
  /** Current value (controlled) */
  value?: string;
  /** Default value for uncontrolled mode */
  defaultValue?: string;
  /** Callback when save is clicked - receives the new value */
  onSave?: (value: string) => void;
  /** Callback when value changes - for controlled mode */
  onChange?: (value: string) => void;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
  /** Callback when edit mode is entered */
  onEdit?: () => void;
  /** Placeholder text shown when value is empty */
  placeholder?: string;
  /** Disables editing */
  disabled?: boolean;
  /** Size variant - uses unified size system */
  size?: TextSize;
  /** Validation state */
  validationState?: ValidationState;
  /** Custom CSS classes for the display text */
  className?: string;
  /** HTML id attribute */
  id?: string;
  /** HTML name attribute */
  name?: string;
  /** Required field */
  required?: boolean;
  /** Auto-focus input when entering edit mode */
  autoFocus?: boolean;
  /** Maximum length of input value */
  maxLength?: number;
  /** Save on Enter key (default: true, only for single-line) */
  saveOnEnter?: boolean;
  /** Save the current edit when the input blurs (default: true). If false, blur will cancel. */
  saveOnBlur?: boolean;
  /** Whether the text is editable. If false, component will not render as input field */
  isEditable?: boolean;
};

/**
 * EditableText - Click-to-edit text display with save/cancel actions
 *
 * Displays text that becomes editable when clicked.
 *
 * @example
 * ```tsx
 * <EditableText
 *   value={name}
 *   onSave={(newValue) => updateName(newValue)}
 *   placeholder="Enter name"
 * />
 * ```
 */
export default function EditableText({
  value: controlledValue,
  defaultValue = '',
  onSave,
  onChange,
  onCancel,
  onEdit,
  placeholder = 'Click to edit',
  disabled = false,
  size = 'base',
  validationState = 'neutral',
  className = '',
  id,
  name,
  required = false,
  autoFocus = true,
  maxLength,
  saveOnEnter = true,
  isEditable = true,
  /** Save the current edit when the input blurs (default: true). If false, blur will cancel. */
  saveOnBlur = true,
}: EditableTextProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  // Offscreen canvas for measuring text width without touching the DOM
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Adjust input width to match content so it doesn't push action icons too far.
  const adjustInputWidth = () => {
    const el = inputRef.current as HTMLInputElement | null;
    if (!el) return;
    // Use an offscreen canvas to measure text width to avoid DOM layout/paint
    canvasRef.current ??= document.createElement('canvas');
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const style = window.getComputedStyle(el);
    // Set canvas font to match the input's computed font so measureText is comparable
    ctx.font = style.font || `${style.fontSize} ${style.fontFamily}`;
    const text = editValue || placeholder || '';
    const measured = Math.ceil(ctx.measureText(text).width) + 16;
    el.style.width = `${Math.max(48, measured)}px`;
  };

  const currentValue = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    if (isEditing && autoFocus && inputRef.current) {
      inputRef.current.focus();
      // Select all text for easy replacement
      inputRef.current.select();
      // ensure width matches current content when entering edit mode
      adjustInputWidth();
    }
    // We intentionally omit adjustInputWidth, editValue, and placeholder from deps here.
    // adjustInputWidth is defined in the component scope and only reads refs/state;
    // it's safe to omit from deps to keep this effect stable.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isEditing, autoFocus]);

  useEffect(() => {
    if (isEditing) adjustInputWidth();
    // adjustInputWidth is stable for this effect and only depends on editValue/placeholder/inputRef,
    // which are already captured by the dependency list.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [editValue, placeholder, isEditing]);

  const handleClick = () => {
    if (disabled || !isEditable) return;
    setEditValue(currentValue);
    setIsEditing(true);
    onEdit?.();
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (!isControlled) {
      setInternalValue(trimmedValue);
    }
    onSave?.(trimmedValue);
    onChange?.(trimmedValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(currentValue);
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (
      e.key === 'Enter' &&
      saveOnEnter &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Check if the blur is due to clicking within our container (Save/Cancel buttons)
    // Use setTimeout to let the click event complete first
    setTimeout(() => {
      // If we're still in editing mode and the related target isn't our container, cancel
      const relatedTarget = e.relatedTarget as HTMLElement;
      const isClickingButton = relatedTarget?.closest(
        '[data-editable-actions]'
      );
      if (isClickingButton) return;

      if (saveOnBlur) {
        handleSave();
      } else {
        handleCancel();
      }
    }, 0);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditValue(e.target.value);
  };

  const sizeConfig = textSizes[size as TextSize] || textSizes['base'];

  if (isEditing) {
    const validationBorder = {
      neutral: 'border-b-foreground/30 focus:border-b-foreground',
      error: 'border-b-error focus:border-b-error',
      success: 'border-b-success focus:border-b-success',
      warning: 'border-b-warning focus:border-b-warning',
    }[validationState];

    const commonProps = {
      ref: inputRef as never,
      value: editValue,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      placeholder,
      disabled,
      id,
      name,
      required,
      maxLength,
      className: `${sizeConfig} border-0 border-b ${validationBorder} bg-transparent py-1 outline-none transition-colors placeholder:text-muted-foreground inline-block`,
    };

    return (
      <div className="flex items-center gap-2">
        <input {...commonProps} type="text" />

        <div data-editable-actions className="flex items-center gap-1">
          <Button
            variant="ghost-error"
            size="icon-xs"
            aria-label="Cancel"
            onClick={handleCancel}
          >
            <X />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Save"
            onClick={handleSave}
          >
            <Check />
          </Button>
        </div>
      </div>
    );
  }

  const displayText = currentValue || placeholder;
  const isPlaceholder = !currentValue;

  // If editing is disabled via `isEditable`, render static text only.
  // This is useful for system-default fields that should be visible but not editable.
  if (!isEditable) {
    return (
      <span
        className={`${sizeConfig} ${className} inline-flex items-center gap-2 py-1 text-left ${isPlaceholder ? 'text-muted-foreground' : 'text-foreground'} w-fit max-w-full`}
      >
        <span className="flex-1">{displayText}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`group ${sizeConfig} ${className} hover:border-b-foreground/30 focus:ring-ring/20 relative inline-flex cursor-text items-center gap-2 border-b border-transparent py-1 text-left transition-all focus:rounded focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${isPlaceholder ? 'text-muted-foreground' : 'text-foreground'} w-fit max-w-full`}
    >
      <span className="flex-1">{displayText}</span>
      <Pencil
        size={getIconPixels('xs', 'desktop')}
        className="shrink-0 opacity-20 transition-opacity group-hover:opacity-60 group-focus:opacity-60"
      />
    </button>
  );
}
